/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'

import {
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiTitle,
  EuiFlexGroup,
  EuiFlexItem,
  EuiButtonEmpty,
  EuiButton,
  EuiForm,
  EuiSpacer,
  EuiHorizontalRule,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { CuiAlert } from '@modules/cui/Alert'
import type { CSP } from '@modules/ui-types/providers'
import type { ResourceType } from '@modules/network-security-lib/types'

import NameField from './fields/NameField'
import DescriptionField from './fields/DescriptionField'
import RegionField from './fields/RegionField'
import CloudProviderField from './fields/CloudProviderField'
import VpceFilterField from './fields/VpceFilterField'
import RemoteClusterField from './fields/RemoteClusterField'
import DeploymentAssociationsField from './fields/DeploymentAssociationsField'
import IncludeByDefaultField from './fields/IncludeByDefaultField'
import ConnectivityField from './fields/ConnectivityField'
import ResourceTypeField from './fields/ResourceTypeField'

import type { TrafficFilterSubFormProps } from './types'

const PrivateConnectionForm: React.FunctionComponent<TrafficFilterSubFormProps> = ({
  onClose,
  ruleset,
  isNewRuleset,
  setRulesetField,
  onSave,
  isSaving,
  error,
}) => {
  const [resourceType, setResourceType] = useState<ResourceType>('hosted')
  const [cloudProvider, setCloudProvider] = useState<CSP>('aws')

  return (
    <EuiFlyout onClose={onClose} size='44rem' aria-labelledby='privateConnectionFormTitle'>
      <EuiFlyoutHeader hasBorder={true}>
        <EuiTitle size='m'>
          <h2 id='privateConnectionFormTitle'>
            {isNewRuleset ? (
              <FormattedMessage
                id='privateConnectionForm.create.title'
                defaultMessage='Create private connection'
              />
            ) : (
              <FormattedMessage
                id='privateConnectionForm.edit.title'
                defaultMessage='Edit private connection'
              />
            )}
          </h2>
        </EuiTitle>
      </EuiFlyoutHeader>

      <EuiFlyoutBody>
        <EuiForm>
          <ResourceTypeField
            value={resourceType}
            onChange={setResourceType}
            isServerlessComingSoon={true}
          />
          <EuiSpacer size='m' />
          <CloudProviderField
            value={cloudProvider}
            onChange={setCloudProvider}
            isDisabled={!isNewRuleset}
          />
          <EuiSpacer size='m' />
          <RegionField
            value={ruleset.region}
            onChange={(value) => setRulesetField('region', value)}
            resourceType='hosted'
            csp={cloudProvider}
            isDisabled={!isNewRuleset}
          />
          <EuiSpacer size='m' />
          <ConnectivityField
            value={ruleset.type}
            onChange={(value) => {
              setRulesetField('type', value)
              setRulesetField('rules', [])
            }}
          />
          <EuiSpacer size='m' />
          <NameField value={ruleset.name} onChange={(value) => setRulesetField('name', value)} />
          <EuiSpacer size='m' />
          <DescriptionField
            value={ruleset.description || ''}
            onChange={(value) => setRulesetField('description', value)}
          />
          <EuiSpacer size='m' />
          {ruleset.type === 'vpce' && (
            <VpceFilterField
              rules={ruleset.rules}
              onChange={(value) => setRulesetField('rules', value)}
              isOptional={cloudProvider !== 'azure'}
            />
          )}
          {ruleset.type === 'remote_cluster' && (
            <RemoteClusterField
              rules={ruleset.rules}
              onChange={(value) => setRulesetField('rules', value)}
            />
          )}

          <EuiHorizontalRule />

          <DeploymentAssociationsField
            regionId={ruleset.region}
            value={ruleset.associations || []}
            onChange={(value) => setRulesetField('associations', value)}
          />
          <EuiSpacer size='m' />
          <IncludeByDefaultField
            value={ruleset.include_by_default || false}
            onChange={(value) => setRulesetField('include_by_default', value)}
          />
        </EuiForm>
        {error && (
          <Fragment>
            <EuiSpacer size='m' />
            <CuiAlert type='danger'>{error}</CuiAlert>
          </Fragment>
        )}
      </EuiFlyoutBody>

      <EuiFlyoutFooter>
        <EuiFlexGroup justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty onClick={onClose} data-test-id='cancel-button'>
              <FormattedMessage id='privateConnectionForm.cancel' defaultMessage='Cancel' />
            </EuiButtonEmpty>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            {isNewRuleset ? (
              <EuiButton fill={true} onClick={onSave} isLoading={isSaving}>
                <FormattedMessage id='privateConnectionForm.create' defaultMessage='Create' />
              </EuiButton>
            ) : (
              <EuiButton fill={true} onClick={onSave} isLoading={isSaving}>
                <FormattedMessage id='privateConnectionForm.save' defaultMessage='Save' />
              </EuiButton>
            )}
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </EuiFlyout>
  )
}

export default PrivateConnectionForm
